/**
 * Tenant-scoped finance & hand-loan records (localStorage via devStorage).
 * Dispatches `workspace-data` on change for live UI updates.
 */
import { useEffect, useState } from 'react';
import { appendDevLog, loadDevJson, saveDevJson } from './devStorage';
import {
  computeEmiHandLoan,
  computeShortTermHandLoan,
  parseHandLoanRepaymentType,
  resolveShortTermDays,
} from './handLoanMath';
import { computeFinance, computeOutPaymentDue, isOutPaymentCollected } from './financeMath';
import { computeCustomerDisplayRef } from './customerDisplay';
import {
  resolveWorkspaceAsOfDate,
  resolveWorkspaceAsOfMs,
  adminEntryIso,
  adminEntryDateOnly,
} from './workspaceAsOf';
import { parseHandLoanInterestType, replayHandLoanBalances, handLoanLedgerAsOfMs, validateHandLoanPaymentAllocation } from './handLoanBalances';
import { computeFacilityUnpaidOverdue } from './overdue';
import { addMonthsClamped, emiDueTimeMs, formatLocalYmd, parseLoanStartDate } from './loanScheduleDates';
import { financeApi, handLoanApi, emiApi, hasAuthToken } from '../api';
import { ApiError } from '../api/client';
import { cacheFinanceApplicantPhoto } from './financePhotoCache';
import { mediaSrc } from './mediaUrl';
import { genCustomerLoginEmail } from './companyProfile';
import { handleSyncRejection } from './periodLockNotice';

const FIN_KEY = 'finance_records_v1';
const HAND_KEY = 'hand_loan_records_v1';

/** Run a backend write only when a backend session exists; never throw to the UI. */
function cloud(fn: () => Promise<unknown>): void {
  if (!hasAuthToken()) return;
  fn().catch((e) => {
    // Closed-year rejections are shown and the server copy restored; otherwise keep the local cache.
    handleSyncRejection(e);
  });
}

/**
 * Persist EMI to server when authenticated. On rejection, remove the local row so
 * client cache cannot keep a payment the API refused (security boundary).
 */
async function persistEmiToServerOrRollback(row: AdminEmiPayment): Promise<RecordPaymentResult> {
  if (!hasAuthToken()) return { ok: true };
  try {
    await emiApi.create(row as unknown as Record<string, unknown>);
    return { ok: true };
  } catch (e) {
    const list = readAdminEmiPayments().filter((p) => p.id !== row.id);
    saveDevJson(ADMIN_EMI_KEY, list);
    emit();
    const msg =
      e instanceof ApiError
        ? e.message
        : e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : 'Server rejected this payment.';
    return { ok: false, error: msg || 'Server rejected this payment.' };
  }
}

export const WORKSPACE_DATA_EVENT = 'workspace-data';

function emit() {
  window.dispatchEvent(new Event(WORKSPACE_DATA_EVENT));
}

/** Notify all `use*` workspace hooks (same bus as finance / hand-loan saves). */
export function notifyWorkspaceDataChanged() {
  emit();
}

const ADMIN_EMI_KEY = 'admin_emi_payments_v1';

export type AdminPaymentEntryType =
  | 'emi'
  | 'advance_emi'
  | 'credit_reversal'
  | 'late_fee'
  | 'lump_sum_close'
  | 'manual_adjustment'
  | 'mixed'
  | 'out_payment'
  | 'overdue_waive';

export type AdminEmiPayment = {
  id: string;
  customerId: string;
  recordId: string;
  facilityTitle: string;
  kind: 'finance' | 'handloan';
  /** Cash / cheque amount received (EMI + fees in one receipt is fine). */
  amount: number;
  /**
   * Installment slots to mark paid relative to the time-based model.
   * +3 = customer prepaid three EMIs; -1 = admin reversal; 0 with late_fee = charges only.
   */
  creditedMonths: number;
  /** Penal / delayed charges (added to customer outstanding on top of principal balance). */
  lateFeeAmount?: number;
  /**
   * Cash applied toward previously logged late / penal charges (reduces outstanding).
   * Distinct from `lateFeeAmount`, which logs a new charge.
   */
  lateFeeCollected?: number;
  /** Admin waiver / reduction of formula-based overdue (audit in `note`). */
  overdueWaivedAmount?: number;
  /** Diary: how many days late (optional). */
  penaltyDays?: number;
  /** When set, facility is treated as fully settled in the synthetic schedule. */
  facilityPayoff?: boolean;
  /** Owner discount / waiver applied on full settlement (contract due − cash received). */
  settlementDiscount?: number;
  entryType?: AdminPaymentEntryType | string;
  paidAt: string;
  note?: string;
  /**
   * Linear contract split (informational): principal / interest share attributed to this row.
   * EMI pool = (EMI × tenor) − principal; spread evenly across months for display & audit.
   */
  principalPortion?: number;
  interestPortion?: number;
  /**
   * Hand loan payment allocation chosen by the operator:
   * interest-only | principal-only | both (explicit split).
   */
  paymentAllocation?: 'interest' | 'principal' | 'both' | string;
  /**
   * Local finance shop: flexible EMI — amount received may differ from contract EMI.
   * Shortfall or credit is stored here after this payment (positive = still owed, negative = prepaid credit).
   */
  carriedAfter?: number;
  /** When true, `amount` drives EMI balance (not manual slot credits). */
  flexibleEmi?: boolean;
  /** cash = physical cash; online = company bank; split = both */
  paymentVia?: 'cash' | 'online' | 'split' | '';
  companyBankId?: string;
  companyBankLabel?: string;
  paidBy?: string;
  cashAmount?: number;
  bankAmount?: number;
};

export function readAdminEmiPayments(): AdminEmiPayment[] {
  return loadDevJson<AdminEmiPayment[]>(ADMIN_EMI_KEY, []);
}

function extraPaidMonthsByRecordId(): Record<string, number> {
  const m: Record<string, number> = {};
  for (const p of readAdminEmiPayments()) {
    if (p.facilityPayoff || p.flexibleEmi || p.carriedAfter !== undefined) continue;
    const c = Math.trunc(Number(p.creditedMonths) || 0);
    if (c === 0) continue;
    m[p.recordId] = (m[p.recordId] ?? 0) + c;
  }
  return m;
}

function facilityPayoffRecordIds(): Set<string> {
  const s = new Set<string>();
  for (const p of readAdminEmiPayments()) {
    if (p.facilityPayoff) s.add(p.recordId);
  }
  return s;
}

const MONTH_MS_MODEL = 30.44 * 24 * 60 * 60 * 1000;

/** Running balance after replaying calendar EMI charges and flexible payments. */
export type FlexibleEmiState = {
  /** Positive = customer still owes; negative = prepaid credit toward next EMI. */
  amountOwed: number;
  /** How many monthly EMI charges have been applied by calendar. */
  monthsCharged: number;
  /** Installment cycles fully settled (for schedule / counts). */
  paidCycles: number;
  isComplete: boolean;
  isPayoff: boolean;
};

function facilityPaymentsChronological(recordId: string, payments: AdminEmiPayment[]): AdminEmiPayment[] {
  return payments
    .filter((p) => p.recordId === recordId)
    .sort((a, b) => paymentEventTime(a.paidAt) - paymentEventTime(b.paidAt));
}

/** Receipt timestamp for replay; invalid dates are treated as earliest so cash is never dropped. */
function paymentEventTime(paidAt: string): number {
  const t = new Date(paidAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

function installmentDueTime(f: UnifiedLoanFacility, monthIndex: number): number {
  return emiDueTimeMs(f.startDate, monthIndex);
}


function handEmiCashApplied(f: UnifiedLoanFacility, payments: AdminEmiPayment[]): number {
  const emi = Math.max(0, Math.round(Number(f.emi) || 0));
  return payments
    .filter((p) => p.recordId === f.recordId && !p.facilityPayoff)
    .reduce((sum, p) => sum + emiCashAppliedFromPayment(p, emi), 0);
}

export function emiCashAppliedFromPayment(p: AdminEmiPayment, emi: number): number {
  if (p.entryType === 'out_payment' || p.entryType === 'overdue_waive') return 0;
  const collected = Math.max(0, Number(p.lateFeeCollected) || 0);
  const charged = Math.max(0, Number(p.lateFeeAmount) || 0);
  const cash = Math.max(0, Number(p.amount) || 0);
  const credited = Math.trunc(Number(p.creditedMonths) || 0);
  // Collect EMI uses separate fields: `amount` = EMI/loan cash, `lateFeeCollected` = extra late cash.
  // Only legacy single-field rows carved lateFeeAmount out of `amount`.
  const lateCarve = collected > 0 ? 0 : charged;
  const emiCash = Math.max(0, cash - lateCarve);
  // Running-balance (flexible) rows always use actual cash — never creditedMonths × EMI.
  if (p.flexibleEmi === true || p.carriedAfter !== undefined) {
    return emiCash;
  }
  if (cash > 0) {
    return emiCash;
  }
  // Legacy slot-only rows (no cash amount recorded).
  if (credited !== 0) {
    return credited * emi;
  }
  return 0;
}

/** Total EMI cash applied to a facility (excludes late-fee portions). Always uses actual receipts — never EMI×tenor on settlement. */
export function cashCollectedForFacility(
  f: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  _payoffs?: Set<string>
): number {
  return facilityPaymentsChronological(f.recordId, payments).reduce(
    (sum, p) => sum + emiCashAppliedFromPayment(p, f.emi),
    0
  );
}

/** Economic interest earned = cash collected toward loan − principal (floored at 0). */
export function economicInterestForFacility(
  f: UnifiedLoanFacility,
  payments: AdminEmiPayment[]
): number {
  const principal = Math.max(0, Math.round(Number(f.principal) || 0));
  const cash = cashCollectedForFacility(f, payments);
  return Math.max(0, Math.round(cash - principal));
}

export type FacilityDueSummary = {
  recordId: string;
  title: string;
  kind: 'finance' | 'handloan';
  contractEmi: number;
  periods: number;
  startDate: string;
  /** Amount due right now (includes carry-forward deficit). */
  currentDueAmount: number;
  /** + shortfall owed, − prepaid credit. */
  carriedBalance: number;
  creditBalance: number;
  deficitBalance: number;
  cashCollected: number;
  nextDueDate: string;
  contractTotal: number;
  /** Remaining contract obligation (unpaid installments + any carry deficit). */
  remainingContract: number;
};

/** Live due / carry-forward snapshot for one facility. */
export function getFacilityDueSummary(
  f: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  payoffs?: Set<string>
): FacilityDueSummary {
  const pmt = payments ?? readAdminEmiPayments();
  const payoffSet = payoffs ?? reducePaymentMaps(pmt).payoffs;
  const flex = replayFlexibleEmiState(f, pmt);
  const paidSlots = paidSlotsForFacility(f, payoffSet, 0, pmt);
  const rows = buildFacilityInstallmentRows(f, paidSlots, pmt);
  const nextDue = rows.find((r) => r.status === 'Due');
  const nextSched = rows.find((r) => r.status === 'Scheduled');
  const nextUnpaid = nextDue ?? nextSched;
  const contractEmi = Math.round(Math.max(0, f.emi));
  const carried = Math.round(flex.amountOwed);
  const remainingInst = Math.max(0, f.periods - paidSlots);
  return {
    recordId: f.recordId,
    title: f.title,
    kind: f.kind,
    contractEmi,
    periods: f.periods,
    startDate: f.startDate,
    currentDueAmount: Math.max(0, carried),
    carriedBalance: carried,
    creditBalance: carried < 0 ? Math.abs(carried) : 0,
    deficitBalance: carried > 0 ? carried : 0,
    cashCollected: Math.round(cashCollectedForFacility(f, pmt, payoffSet)),
    nextDueDate: nextUnpaid?.date || '—',
    contractTotal: contractEmi * Math.max(1, f.periods),
    remainingContract:
      f.kind === 'handloan'
        ? Math.max(0, carried) + remainingInst * contractEmi
        : remainingFinanceContractCash(f, flex),
  };
}

/** Human-readable carry-forward line for UI. */
export function formatEmiDueExplanation(summary: FacilityDueSummary): string {
  const { contractEmi, currentDueAmount, carriedBalance, creditBalance } = summary;
  if (creditBalance > 0) {
    return `Contract EMI ₹${contractEmi.toLocaleString('en-IN')} · Due now ₹0 (₹${creditBalance.toLocaleString('en-IN')} credit from overpayment)`;
  }
  if (carriedBalance > contractEmi) {
    const carry = carriedBalance - contractEmi;
    return `Contract EMI ₹${contractEmi.toLocaleString('en-IN')} · Due now ₹${currentDueAmount.toLocaleString('en-IN')} (includes ₹${carry.toLocaleString('en-IN')} carried forward)`;
  }
  if (carriedBalance > 0 && carriedBalance < contractEmi) {
    return `Contract EMI ₹${contractEmi.toLocaleString('en-IN')} · Due now ₹${currentDueAmount.toLocaleString('en-IN')} (partial month balance)`;
  }
  return `Monthly EMI ₹${contractEmi.toLocaleString('en-IN')}`;
}

/** Newest workspace entry first — shared by customers, finance, and hand loans. */
export function sortByEntryDateDesc<T extends { createdAt: string }>(records: T[]): T[] {
  return [...records].sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    const sa = Number.isFinite(ta) ? ta : 0;
    const sb = Number.isFinite(tb) ? tb : 0;
    return sb - sa;
  });
}

export function formatWorkspaceEntryDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-IN') : '—';
}

/** Earliest facility entry date — when this borrower was first added to the workspace. */
export function customerAccountCreatedAt(c: UnifiedCustomer): string {
  const times = c.facilities
    .map((f) => new Date(f.startDate).getTime())
    .filter((t) => Number.isFinite(t));
  if (!times.length) return c.createdAt || '';
  return new Date(Math.min(...times)).toISOString();
}

type ReplayEvent =
  | { time: number; kind: 'month'; monthIndex: number }
  | { time: number; kind: 'pay'; payment: AdminEmiPayment };

/**
 * After chronological replay, derive paid installment count from cash (FIFO: oldest
 * installment first). Every rupee of EMI cash counts once, so paid installments are
 * floor(totalCash / EMI) regardless of how many installments have fallen due.
 */
function finalizePaidCyclesAfterReplay(
  amountOwed: number,
  paidCycles: number,
  periods: number,
  emi: number,
  totalCash: number
): { amountOwed: number; paidCycles: number } {
  if (emi <= 0) return { amountOwed, paidCycles };
  const slotsFromCash = Math.max(0, Math.min(periods, Math.floor(totalCash / emi)));
  return { amountOwed, paidCycles: slotsFromCash };
}

/** Contract cash still to collect on a finance facility: arrears + installments not yet charged. */
export function remainingFinanceContractCash(f: UnifiedLoanFacility, flex: FlexibleEmiState): number {
  const emi = Math.max(0, Math.round(Number(f.emi) || 0));
  const periods = Math.max(1, Math.floor(Number(f.periods) || 1));
  if (flex.isPayoff) return 0;
  const notYetCharged = Math.max(0, periods - flex.monthsCharged);
  return Math.max(0, Math.round(flex.amountOwed) + notYetCharged * emi);
}

/**
 * Replay calendar EMI charges and payments chronologically — local finance shop model.
 * Each month adds contract EMI to amount owed; receipts reduce it (any amount, not fixed).
 * Unpaid balance carries into the next month; overpayment becomes credit.
 * All admin-recorded payments apply regardless of receipt date (cash received is real).
 */
export function replayFlexibleEmiState(
  f: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  asOf = resolveWorkspaceAsOfMs({ allowWallClockFallback: true })
): FlexibleEmiState {
  const emi = Math.max(0, Math.round(Number(f.emi) || 0));
  const periods = Math.max(1, Math.floor(Number(f.periods) || 1));

  if (f.repaymentType === 'short_term') {
    const facilityPayments = facilityPaymentsChronological(f.recordId, payments);
    if (facilityPayments.some((p) => p.facilityPayoff)) {
      return { amountOwed: 0, monthsCharged: 1, paidCycles: 1, isComplete: true, isPayoff: true };
    }
    // Daily hand-loan model: due = principal out + interest accrued to date — NOT full 60-day contract lump.
    // Paying ₹50k same-day must clear the file even if tenure was entered as 60 days.
    const bal = replayHandLoanBalances({
      principal: Math.max(0, Math.round(Number(f.principal) || 0)),
      rate: Number(f.interestRate) || 0,
      interestType: parseHandLoanInterestType(f.interestType),
      repaymentType: 'short_term',
      startDate: f.startDate,
      termDays: f.termDays,
      periods: 1,
      payments: facilityPayments,
      asOf: handLoanLedgerAsOfMs(f.startDate, facilityPayments),
    });
    const amountOwed = Math.max(0, bal.principalOutstanding + bal.interestDue);
    const isComplete = amountOwed <= 0.5;
    return {
      amountOwed,
      monthsCharged: 1,
      paidCycles: isComplete ? 1 : 0,
      isComplete,
      isPayoff: false,
    };
  }

  if (f.kind === 'handloan') {
    const facilityPayments = facilityPaymentsChronological(f.recordId, payments);
    if (facilityPayments.some((p) => p.facilityPayoff)) {
      return { amountOwed: 0, monthsCharged: periods, paidCycles: periods, isComplete: true, isPayoff: true };
    }
    const bal = replayHandLoanBalances({
      principal: Math.max(0, Math.round(Number(f.principal) || 0)),
      rate: Number(f.interestRate) || 0,
      interestType: parseHandLoanInterestType(f.interestType),
      repaymentType: 'emi',
      startDate: f.startDate,
      periods: f.periods,
      payments: facilityPayments,
      asOf: handLoanLedgerAsOfMs(f.startDate, facilityPayments),
    });
    const amountOwed = Math.max(0, bal.principalOutstanding + bal.interestDue);
    const isComplete = amountOwed <= 0.5;
    // Schedule slots are cleared oldest-first by cash received (interest + principal), like finance loans.
    const slotsFromCash =
      emi > 0 ? Math.min(periods, Math.floor((handEmiCashApplied(f, facilityPayments) + 0.005) / emi)) : 0;
    return {
      amountOwed,
      monthsCharged: periods,
      paidCycles: isComplete ? periods : slotsFromCash,
      isComplete,
      isPayoff: false,
    };
  }

  const facilityPayments = facilityPaymentsChronological(f.recordId, payments);
  if (facilityPayments.some((p) => p.facilityPayoff) || payments.some((p) => p.recordId === f.recordId && p.facilityPayoff)) {
    return { amountOwed: 0, monthsCharged: periods, paidCycles: periods, isComplete: true, isPayoff: true };
  }

  const regularPayments = facilityPayments.filter((p) => !p.facilityPayoff);
  const totalCash = regularPayments.reduce((sum, p) => sum + emiCashAppliedFromPayment(p, emi), 0);

  const events: ReplayEvent[] = [];
  for (let m = 1; m <= periods; m++) {
    const dueAt = installmentDueTime(f, m);
    if (dueAt > 0 && dueAt <= asOf) {
      events.push({ time: dueAt, kind: 'month', monthIndex: m });
    }
  }
  for (const p of regularPayments) {
    events.push({ time: paymentEventTime(p.paidAt), kind: 'pay', payment: p });
  }
  events.sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    // On the same day, charge EMI before applying receipts.
    if (a.kind === 'month' && b.kind === 'pay') return -1;
    if (a.kind === 'pay' && b.kind === 'month') return 1;
    return 0;
  });

  let amountOwed = 0;
  let monthsCharged = 0;
  let paidCycles = 0;

  for (const ev of events) {
    if (ev.kind === 'month') {
      amountOwed += emi;
      monthsCharged = ev.monthIndex;
      if (amountOwed <= 0) paidCycles = ev.monthIndex;
    } else {
      amountOwed -= emiCashAppliedFromPayment(ev.payment, emi);
    }
  }

  const finalized = finalizePaidCyclesAfterReplay(amountOwed, paidCycles, periods, emi, totalCash);
  amountOwed = finalized.amountOwed;
  paidCycles = finalized.paidCycles;

  const isComplete = paidCycles >= periods && amountOwed <= 0;
  if (isComplete) {
    paidCycles = periods;
    amountOwed = Math.min(0, amountOwed);
  }

  return { amountOwed, monthsCharged, paidCycles, isComplete, isPayoff: false };
}

/** Preview how a new receipt would change the running balance (before saving). */
export function previewFlexiblePayment(
  f: UnifiedLoanFacility,
  payments: AdminEmiPayment[],
  input: { amount: number; lateFeeAmount: number; payoff?: boolean }
): {
  beforeOwed: number;
  afterOwed: number;
  emiApplied: number;
  cyclesCleared: number;
  carriedAfter: number;
} {
  const emi = Math.max(0, Math.round(Number(f.emi) || 0));
  const before = replayFlexibleEmiState(f, payments);
  const cash = Math.max(0, Number(input.amount) || 0);
  // Preview callers pass EMI amount + late separately; do not carve late out of EMI cash.
  const emiApplied = input.payoff ? Math.max(0, before.amountOwed) : cash;

  if (input.payoff) {
    return {
      beforeOwed: before.amountOwed,
      afterOwed: 0,
      emiApplied,
      cyclesCleared: Math.max(0, before.monthsCharged - before.paidCycles),
      carriedAfter: 0,
    };
  }

  const afterOwed = before.amountOwed - emiApplied;
  // cyclesCleared is display-only; flexible ledger never stores creditedMonths slots.
  const cyclesCleared =
    before.amountOwed > 0 && afterOwed <= 0
      ? 1 + (afterOwed < 0 ? Math.floor(Math.abs(afterOwed) / Math.max(1, emi)) : 0)
      : 0;

  return {
    beforeOwed: before.amountOwed,
    afterOwed,
    emiApplied,
    cyclesCleared,
    carriedAfter: afterOwed,
  };
}

function paymentsForFacility(recordId: string): AdminEmiPayment[] {
  return readAdminEmiPayments().filter((p) => p.recordId === recordId);
}

/** Live collection / contract snapshot for one facility (reads current admin ledger). */
export type FacilityCollectionSnapshot = {
  recordId: string;
  principal: number;
  emiRounded: number;
  periods: number;
  paidMonths: number;
  remainingInstallments: number;
  monthsElapsedNatural: number;
  creditSlots: number;
  /** Ledger already has a closure row for this facility. */
  isLedgerClosed: boolean;
  /** Schedule shows all installments paid (before optional late-only tail). */
  isScheduleComplete: boolean;
  /** Contractual cash still due from unpaid EMI slots (remaining × EMI). */
  remainingScheduledCash: number;
  /** Sum of late / penal lines posted for this facility. */
  accruedLateFees: number;
  /** Minimum receipt to accept a full settlement row (remaining schedule cash + late fees). */
  settlementCashFloor: number;
  /** max(0, EMI×tenor − principal) — total interest pool under linear contract view. */
  lifetimeInterestEstimate: number;
  /** Interest attributed to paid slots (linear proration). */
  interestRecognizedToDate: number;
  /** Principal attributed to paid slots (linear proration). */
  principalPaidModelLinear: number;
  /** Principal still “open” under the same linear view (for display). */
  principalOutstandingLinear: number;
  canOpenPaymentUi: boolean;
  canCreditEmiSlots: boolean;
  /** Running balance: + shortfall owed, − prepaid credit (local finance shop carryover). */
  carriedBalance: number;
  /** Amount due right now (may differ from contract EMI when balance carries). */
  currentDueAmount: number;
  /** Hand loan: outstanding principal after allocation-aware payments. */
  handPrincipalOutstanding?: number;
  /** Hand loan: unpaid accrued interest. */
  handInterestDue?: number;
  /** Finance: out-payment still pending (down payment + OP interest). */
  outPaymentPending?: boolean;
  /** Finance: total due if out payment collected today (principal + accrued OP interest). */
  outPaymentDueTotal?: number;
};

export function getFacilityCollectionSnapshot(f: UnifiedLoanFacility): FacilityCollectionSnapshot {
  const principal = Math.max(0, Math.round(Number(f.principal) || 0));
  const periods = Math.max(1, Math.floor(Number(f.periods) || 1));
  const emiRounded = Math.max(0, Math.round(Number(f.emi) || 0));
  const start = new Date(f.startDate).getTime();
  const payments = paymentsForFacility(f.recordId);
  const flex = replayFlexibleEmiState(f, payments);
  const payoffs = facilityPayoffRecordIds();
  const isLedgerClosed = payoffs.has(f.recordId);
  const paidMonths = flex.paidCycles;
  const remainingInstallments = Math.max(0, periods - paidMonths);
  const accruedLateFees = Math.round(computeFacilityUnpaidOverdue(f, payments).penaltyAmount);
  let carriedBalance = Math.round(flex.amountOwed);
  let currentDueAmount = Math.max(0, carriedBalance);
  let principalOutstandingLinear = 0;
  let interestRecognizedToDate = 0;
  let principalPaidModelLinear = 0;
  let handPrincipalOutstanding: number | undefined;
  let handInterestDue: number | undefined;

  // Hand loans: ledger clock = max(start, payments, working/books date).
  const handAsOf =
    f.kind === 'handloan'
      ? handLoanLedgerAsOfMs(
          f.startDate,
          payments,
          resolveWorkspaceAsOfDate() || undefined
        ) || resolveWorkspaceAsOfMs({ allowWallClockFallback: true })
      : resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
  const monthsElapsedNatural = Number.isFinite(start)
    ? Math.max(0, Math.floor((handAsOf - start) / MONTH_MS_MODEL))
    : 0;

  if (f.kind === 'handloan') {
    const bal = replayHandLoanBalances({
      principal,
      rate: Number(f.interestRate) || 0,
      interestType: parseHandLoanInterestType(f.interestType),
      repaymentType: f.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: f.startDate,
      termDays: f.termDays,
      periods,
      payments,
      asOf: handAsOf,
    });
    // Mid-year migration: seed unpaid interest recorded at cutover (once), then accrue normally.
    const openingInterestSeed = Math.max(0, Math.round(Number(f.openingInterestDue) || 0));
    const hasPostCutoverInterestPay = payments.some(
      (p) => Math.max(0, Number(p.interestPortion) || 0) > 0 || p.facilityPayoff
    );
    handPrincipalOutstanding = bal.principalOutstanding;
    handInterestDue = bal.interestDue + (hasPostCutoverInterestPay ? 0 : openingInterestSeed);
    principalOutstandingLinear = bal.principalOutstanding;
    principalPaidModelLinear = Math.max(0, principal - bal.principalOutstanding);
    interestRecognizedToDate = bal.interestPaidTotal;
    carriedBalance = (handPrincipalOutstanding || 0) + (handInterestDue || 0);
    currentDueAmount = Math.max(0, carriedBalance);
  } else {
    const contractualTotal = emiRounded * periods;
    const lifetimeInterestEstimate = Math.max(0, Math.round(contractualTotal - principal));
    if (flex.isPayoff) {
      // Early / full settlement: recognize only interest actually collected in cash, not full tenure.
      interestRecognizedToDate = economicInterestForFacility(f, payments);
      principalPaidModelLinear = Math.min(principal, cashCollectedForFacility(f, payments));
      principalOutstandingLinear = Math.max(0, principal - principalPaidModelLinear);
    } else {
      interestRecognizedToDate =
        periods > 0 ? Math.round((lifetimeInterestEstimate * paidMonths) / periods) : 0;
      principalPaidModelLinear = periods > 0 ? Math.round((principal * paidMonths) / periods) : 0;
      principalOutstandingLinear = Math.max(0, principal - principalPaidModelLinear);
    }
  }

  const remainingScheduledCash =
    f.kind === 'handloan' ? Math.max(0, carriedBalance) : remainingFinanceContractCash(f, flex);
  // Full settlement must cover unpaid principal (and for EMI finance, remaining schedule cash).
  // Never allow close on "current month due" alone — that let underpaid files close after 1 EMI.
  const cashTowardLoan = cashCollectedForFacility(f, payments);
  const unpaidPrincipal = Math.max(0, principal - Math.round(cashTowardLoan));
  const remainingObligation =
    f.kind === 'handloan'
      ? Math.max(0, carriedBalance)
      : Math.max(unpaidPrincipal, Math.max(0, remainingScheduledCash), Math.max(0, Math.round(flex.amountOwed)));
  const settlementCashFloor = remainingObligation + accruedLateFees;
  const contractualTotal = emiRounded * periods;
  const lifetimeInterestEstimate =
    f.kind === 'handloan'
      ? Math.max(0, handInterestDue ?? 0)
      : Math.max(0, Math.round(contractualTotal - principal));
  const isScheduleComplete =
    f.kind === 'handloan'
      ? (handPrincipalOutstanding ?? 0) <= 0 && (handInterestDue ?? 0) <= 0
      : flex.isComplete;
  const canOpenPaymentUi = !isLedgerClosed;
  const canCreditEmiSlots = canOpenPaymentUi && !isScheduleComplete;
  const extra = extraPaidMonthsByRecordId();
  const creditSlots = Math.trunc(extra[f.recordId] ?? 0);

  let outPaymentPending = false;
  let outPaymentDueTotal = 0;
  if (f.kind === 'finance') {
    const opPrincipal = Math.max(0, Math.round(Number(f.outPayment) || 0));
    if (opPrincipal > 0 && !isOutPaymentCollected(f.recordId, payments, opPrincipal)) {
      outPaymentPending = true;
      const asOfYmd = resolveWorkspaceAsOfDate() || adminEntryDateOnly();
      const due = computeOutPaymentDue({
        outPayment: opPrincipal,
        opRate: Number(f.opInterest) || 0,
        startDate: f.startDate,
        asOfDate: asOfYmd,
      });
      outPaymentDueTotal = due.total;
    }
  }

  return {
    recordId: f.recordId,
    principal,
    emiRounded,
    periods,
    paidMonths,
    remainingInstallments,
    monthsElapsedNatural,
    creditSlots,
    isLedgerClosed,
    isScheduleComplete,
    remainingScheduledCash,
    accruedLateFees,
    settlementCashFloor,
    lifetimeInterestEstimate,
    interestRecognizedToDate,
    principalPaidModelLinear,
    principalOutstandingLinear,
    canOpenPaymentUi,
    canCreditEmiSlots,
    carriedBalance,
    currentDueAmount,
    handPrincipalOutstanding,
    handInterestDue,
    outPaymentPending,
    outPaymentDueTotal,
  };
}

/**
 * True when this facility still needs collection work: unpaid EMI slots, late/penal balance to post against,
 * or advance EMI credits that could be reversed. When false (and ledger is still open), every EMI is
 * satisfied and there is nothing meaningful left to record.
 */
export function facilityHasActionableCollectionWork(s: FacilityCollectionSnapshot): boolean {
  if (s.outPaymentPending) return true;
  if (s.handPrincipalOutstanding != null || s.handInterestDue != null) {
    return (s.handPrincipalOutstanding ?? 0) > 0 || (s.handInterestDue ?? 0) > 0 || s.accruedLateFees > 0;
  }
  return s.remainingInstallments > 0 || s.carriedBalance > 0 || s.accruedLateFees > 0;
}

/** Full vs minimum receipt for payoff — owners may discount interest / remaining schedule, not principal+late. */
export function computePayoffSettlementBounds(
  f: UnifiedLoanFacility,
  input: { lateFeeAmount?: number; lateFeeCollected?: number; paidAt?: string | null } = {}
): { fullDue: number; minimumDue: number; unpaidPrincipal: number } {
  const snap = getFacilityCollectionSnapshot(f);
  const late = Math.max(0, Number(input.lateFeeAmount) || 0);
  const lateCollected = Math.max(0, Number(input.lateFeeCollected) || 0);
  const lateNet = Math.max(0, snap.accruedLateFees - lateCollected) + late;
  const payments = paymentsForFacility(f.recordId);

  if (f.kind === 'handloan') {
    const asOf = handLoanLedgerAsOfMs(f.startDate, payments, input.paidAt || undefined);
    const bal = replayHandLoanBalances({
      principal: Math.max(0, Math.round(Number(f.principal) || 0)),
      rate: Number(f.interestRate) || 0,
      interestType: parseHandLoanInterestType(f.interestType),
      repaymentType: f.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: f.startDate,
      termDays: f.termDays,
      periods: f.periods,
      payments,
      asOf,
    });
    const principalOut = Math.max(0, bal.principalOutstanding);
    const interestDue = Math.max(0, bal.interestDue);
    return {
      fullDue: principalOut + interestDue + lateNet,
      minimumDue: principalOut + lateNet,
      unpaidPrincipal: principalOut,
    };
  }

  const preCut = Math.max(0, Number(f.preCutoverCollected) || 0);
  const cashToward = cashCollectedForFacility(f, payments) + preCut;
  const unpaidPrincipal = Math.max(0, snap.principal - Math.round(cashToward));
  const obligation = Math.max(0, snap.settlementCashFloor - snap.accruedLateFees);
  return {
    fullDue: obligation + lateNet,
    minimumDue: unpaidPrincipal + lateNet,
    unpaidPrincipal,
  };
}

export type RecordAdminPaymentInput = Omit<AdminEmiPayment, 'id' | 'paidAt' | 'principalPortion' | 'interestPortion'> & {
  id?: string;
  paidAt?: string;
  /** When provided, strict business rules are enforced against this facility. */
  facility?: UnifiedLoanFacility;
  /** Default true — amount drives EMI balance (local finance shop). Set false for legacy slot credits. */
  flexibleEmi?: boolean;
  /** Explicit portions for hand-loan "Both" allocation (also set on interest/principal-only). */
  principalPortion?: number;
  interestPortion?: number;
};

export type RecordPaymentResult = { ok: true } | { ok: false; error: string };

function linearSplitForMonths(
  principal: number,
  periods: number,
  lifetimeInterest: number,
  months: number
): { principalPortion: number; interestPortion: number } {
  const p = Math.max(1, periods);
  const m = Math.max(0, Math.trunc(months));
  const pr = Math.round((principal * m) / p);
  const intr = Math.round((lifetimeInterest * m) / p);
  return { principalPortion: pr, interestPortion: intr };
}

/** Linear contract split for UI preview (same math as ledger rows). */
export function previewEmiPortionsForFacility(
  f: UnifiedLoanFacility,
  creditedMonths: number
): { principalPortion: number; interestPortion: number } | null {
  const snap = getFacilityCollectionSnapshot(f);
  const m = Math.trunc(Number(creditedMonths) || 0);
  if (m === 0) return null;
  return linearSplitForMonths(snap.principal, snap.periods, snap.lifetimeInterestEstimate, Math.abs(m));
}

export function validateAdminFacilityPayment(
  f: UnifiedLoanFacility,
  input: {
    payoff: boolean;
    creditedMonths: number;
    lateFeeAmount: number;
    lateFeeCollected?: number;
    amount: number;
    flexibleEmi?: boolean;
    /** Payment / books date — hand-loan payoff floor uses this clock (not wall/workspace alone). */
    paidAt?: string | null;
  }
): RecordPaymentResult {
  const snap = getFacilityCollectionSnapshot(f);
  const credited = Math.trunc(Number(input.creditedMonths) || 0);
  const late = Math.max(0, Number(input.lateFeeAmount) || 0);
  const lateCollected = Math.max(0, Number(input.lateFeeCollected) || 0);
  const amt = Math.max(0, Number(input.amount) || 0);
  const payoff = !!input.payoff;
  const flexible = input.flexibleEmi !== false;

  if (snap.isLedgerClosed) {
    return { ok: false, error: 'This facility is already marked closed. No further ledger entries are allowed.' };
  }

  if (!payoff && snap.isScheduleComplete && credited === 0 && late <= 0 && lateCollected <= 0 && amt > 0) {
    return {
      ok: false,
      error:
        'The installment schedule is already complete. Use full settlement for any contractual or late balance, or add late charges before settling.',
    };
  }

  if (!payoff && credited === 0 && late <= 0 && lateCollected <= 0 && amt <= 0) {
    return { ok: false, error: 'Enter the amount received and/or late charges.' };
  }

  if (lateCollected > snap.accruedLateFees + late + 0.5) {
    return {
      ok: false,
      error: `Late charges paid (₹${lateCollected.toLocaleString('en-IN')}) cannot exceed late due ₹${(snap.accruedLateFees + late).toLocaleString('en-IN')}.`,
    };
  }

  if (payoff) {
    const bounds = computePayoffSettlementBounds(f, {
      lateFeeAmount: late,
      lateFeeCollected: lateCollected,
      paidAt: input.paidAt,
    });
    const receiptTotal = amt + lateCollected;
    const lateNet = Math.max(0, snap.accruedLateFees - lateCollected) + late;

    if (bounds.fullDue <= 0.5 && receiptTotal <= 0.5) {
      return { ok: false, error: 'Nothing left to settle on this facility.' };
    }
    if (receiptTotal <= 0.5) {
      return { ok: false, error: 'Enter the settlement amount received.' };
    }
    if (receiptTotal + 0.5 < bounds.minimumDue) {
      return {
        ok: false,
        error: `Settlement must cover at least ₹${bounds.minimumDue.toLocaleString('en-IN')} (outstanding principal ₹${bounds.unpaidPrincipal.toLocaleString('en-IN')}${lateNet > 0 ? ` + late ₹${lateNet.toLocaleString('en-IN')}` : ''}). You entered ₹${receiptTotal.toLocaleString('en-IN')}.`,
      };
    }
    return { ok: true };
  }

  // Same EMI-cash rule as the server: a late fee charged on this receipt is carved out of `amount`.
  const emiCash = lateCollected > 0 ? amt : Math.max(0, amt - late);
  if (f.kind === 'finance' && emiCash > snap.remainingScheduledCash + 0.5) {
    return {
      ok: false,
      error: `₹${emiCash.toLocaleString('en-IN')} is more than the ₹${snap.remainingScheduledCash.toLocaleString('en-IN')} left on this loan's EMI schedule. Use full settlement to close the loan.`,
    };
  }

  if (flexible && amt > 0) {
    return { ok: true };
  }

  if (credited > 0) {
    if (snap.remainingInstallments <= 0) {
      return { ok: false, error: 'All installments are already marked paid. Use full settlement if you need to close late charges or adjust the ledger.' };
    }
    if (credited > snap.remainingInstallments) {
      return {
        ok: false,
        error: `Cannot credit ${credited} EMI slot(s); only ${snap.remainingInstallments} remain on the contract schedule.`,
      };
    }
  }

  if (credited < 0) {
    const extra = Math.max(0, snap.creditSlots);
    if (extra <= 0) {
      return { ok: false, error: 'There are no advance EMI credits on file to reverse for this facility.' };
    }
    if (-credited > extra) {
      return { ok: false, error: `You can reverse at most ${extra} credited slot(s).` };
    }
  }

  return { ok: true };
}

export async function recordAdminEmiPayment(entry: RecordAdminPaymentInput): Promise<RecordPaymentResult> {
  const { facility, ...rest } = entry;
  const lateFee = Math.max(0, Number(entry.lateFeeAmount) || 0);
  const lateCollected = Math.max(0, Number(entry.lateFeeCollected) || 0);
  // When lateFeeCollected is set, `amount` is EMI/loan cash (late is extra). Legacy carved late from amount.
  const lateCarve = lateCollected > 0 ? 0 : lateFee;
  const amount = Math.max(0, Number(entry.amount) || 0);
  let payoff = !!entry.facilityPayoff;
  const isHandAlloc =
    facility?.kind === 'handloan' &&
    !payoff &&
    (entry.paymentAllocation === 'interest' ||
      entry.paymentAllocation === 'principal' ||
      entry.paymentAllocation === 'both');
  const isOutPayment = entry.entryType === 'out_payment';
  const useFlexible = entry.flexibleEmi !== false && !payoff && facility && !isHandAlloc && !isOutPayment;

  let credited = Math.trunc(Number(entry.creditedMonths) || 0);
  let carriedAfter: number | undefined;
  let flexibleEmi: boolean | undefined;
  let principalPortion: number | undefined;
  let interestPortion: number | undefined;

  if (isHandAlloc && facility) {
    const paidAtIso = rest.paidAt ?? adminEntryIso();
    const asOf = handLoanLedgerAsOfMs(facility.startDate, paymentsForFacility(facility.recordId), paidAtIso);
    const balBefore = replayHandLoanBalances({
      principal: facility.principal,
      rate: Number(facility.interestRate) || 0,
      interestType: parseHandLoanInterestType(facility.interestType),
      repaymentType: facility.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: facility.startDate,
      termDays: facility.termDays,
      periods: facility.periods,
      payments: paymentsForFacility(facility.recordId),
      asOf,
    });
    const interestDue = balBefore.interestDue;
    const principalOut = balBefore.principalOutstanding;
    const checked = validateHandLoanPaymentAllocation({
      allocation: entry.paymentAllocation as 'interest' | 'principal' | 'both',
      totalAmount: amount,
      interestAmount: entry.interestPortion,
      principalAmount: entry.principalPortion,
      interestDue,
      principalOutstanding: principalOut,
    });
    if (!checked.ok) return checked;
    interestPortion = checked.interestPortion;
    principalPortion = checked.principalPortion;
    const afterBal = replayHandLoanBalances({
      principal: facility.principal,
      rate: Number(facility.interestRate) || 0,
      interestType: parseHandLoanInterestType(facility.interestType),
      repaymentType: facility.repaymentType === 'short_term' ? 'short_term' : 'emi',
      startDate: facility.startDate,
      termDays: facility.termDays,
      periods: facility.periods,
      payments: [
        ...paymentsForFacility(facility.recordId),
        {
          paidAt: paidAtIso,
          amount,
          lateFeeAmount: lateFee,
          lateFeeCollected: lateCollected > 0 ? lateCollected : undefined,
          paymentAllocation: entry.paymentAllocation,
          principalPortion,
          interestPortion,
        },
      ],
      asOf: handLoanLedgerAsOfMs(facility.startDate, paymentsForFacility(facility.recordId), paidAtIso),
    });
    carriedAfter = afterBal.principalOutstanding + afterBal.interestDue;
    credited = 0;
    // Same-day / early repay that clears books → close file (ignore leftover tenure days).
    if (afterBal.principalOutstanding <= 0.5 && afterBal.interestDue <= 0.5) {
      payoff = true;
      carriedAfter = 0;
    }
  } else if (useFlexible && facility) {
    const preview = previewFlexiblePayment(facility, paymentsForFacility(facility.recordId), {
      amount,
      lateFeeAmount: lateCarve,
    });
    carriedAfter = Math.round(preview.carriedAfter);
    flexibleEmi = true;
    credited = 0;
  } else if (isOutPayment && facility) {
    principalPortion = Math.max(0, Math.round(Number(entry.principalPortion) || 0));
    interestPortion = Math.max(0, Math.round(Number(entry.interestPortion) || 0));
    credited = 0;
    flexibleEmi = false;
  } else if (!payoff) {
    credited = Math.trunc(Number(entry.creditedMonths) || 0);
  } else {
    credited = 0;
  }

  if (facility && !isHandAlloc && !isOutPayment) {
    const v = validateAdminFacilityPayment(facility, {
      payoff,
      creditedMonths: credited,
      lateFeeAmount: lateFee,
      lateFeeCollected: lateCollected,
      amount,
      flexibleEmi: useFlexible,
      paidAt: rest.paidAt,
    });
    if (!v.ok) return v;
  } else if (!facility && !payoff && credited === 0 && lateFee <= 0 && lateCollected <= 0 && amount <= 0) {
    return { ok: false, error: 'Nothing to record.' };
  } else if (facility && isHandAlloc) {
    const snap = getFacilityCollectionSnapshot(facility);
    if (snap.isLedgerClosed) {
      return { ok: false, error: 'This facility is already marked closed. No further ledger entries are allowed.' };
    }
  }

  const snap = facility ? getFacilityCollectionSnapshot(facility) : null;
  if (!isHandAlloc && facility && snap && !snap.isLedgerClosed) {
    const life = snap.lifetimeInterestEstimate;
    const prin = snap.principal;
    const p = snap.periods;
    if (payoff && snap.remainingInstallments >= 0) {
      if (facility.kind === 'handloan') {
        // Shop flow: interest-first from this receipt, remainder to principal (then file closes).
        const priorPays = paymentsForFacility(facility.recordId);
        const asOf = handLoanLedgerAsOfMs(facility.startDate, priorPays, rest.paidAt);
        const bal = replayHandLoanBalances({
          principal: Math.max(0, Math.round(Number(facility.principal) || 0)),
          rate: Number(facility.interestRate) || 0,
          interestType: parseHandLoanInterestType(facility.interestType),
          repaymentType: facility.repaymentType === 'short_term' ? 'short_term' : 'emi',
          startDate: facility.startDate,
          termDays: facility.termDays,
          periods: facility.periods,
          payments: priorPays,
          asOf,
        });
        const iDue = Math.max(0, bal.interestDue);
        const pOut = Math.max(0, bal.principalOutstanding);
        interestPortion = Math.min(amount, iDue);
        principalPortion = Math.min(Math.max(0, amount - interestPortion), pOut);
      } else {
        // Early settlement: book interest from actual cash economics, not remaining EMI×months.
        const cashToward = Math.max(0, amount - lateCarve);
        const prior = facilityPaymentsChronological(facility.recordId, readAdminEmiPayments());
        const priorCash = prior.reduce((s, p) => s + emiCashAppliedFromPayment(p, facility.emi), 0);
        const priorInterest = prior.reduce((s, p) => s + Math.max(0, Number(p.interestPortion) || 0), 0);
        const totalCash = priorCash + cashToward;
        const totalInterestEconomic = Math.max(0, Math.round(totalCash - prin));
        interestPortion = Math.max(0, totalInterestEconomic - priorInterest);
        principalPortion = Math.max(0, cashToward - interestPortion);
      }
      carriedAfter = 0;
    } else if (!payoff && credited !== 0) {
      const split = linearSplitForMonths(prin, p, life, Math.abs(credited));
      principalPortion = credited < 0 ? -split.principalPortion : split.principalPortion;
      interestPortion = credited < 0 ? -split.interestPortion : split.interestPortion;
    } else if (!payoff && useFlexible && amount > 0) {
      const split = linearSplitForMonths(prin, p, life, 1);
      const ratio = snap.emiRounded > 0 ? Math.min(1, Math.max(0, (amount - lateCarve) / snap.emiRounded)) : 0;
      principalPortion = Math.round(split.principalPortion * ratio);
      interestPortion = Math.round(split.interestPortion * ratio);
    }
  }

  let settlementDiscount: number | undefined;
  if (payoff && facility) {
    const bounds = computePayoffSettlementBounds(facility, {
      lateFeeAmount: lateFee,
      lateFeeCollected: lateCollected,
      paidAt: rest.paidAt,
    });
    const receiptTotal = amount + lateCollected;
    const discount = Math.max(0, Math.round(bounds.fullDue - receiptTotal));
    if (discount > 0) settlementDiscount = discount;
  }

  const list = readAdminEmiPayments();
  const row: AdminEmiPayment = {
    id: rest.id ?? crypto.randomUUID(),
    customerId: rest.customerId,
    recordId: rest.recordId,
    facilityTitle: rest.facilityTitle,
    kind: rest.kind,
    amount,
    creditedMonths: payoff ? 0 : credited,
    lateFeeAmount: lateFee > 0 ? lateFee : undefined,
    lateFeeCollected: lateCollected > 0 ? lateCollected : undefined,
    penaltyDays:
      rest.penaltyDays != null && Number.isFinite(Number(rest.penaltyDays))
        ? Math.max(0, Math.floor(Number(rest.penaltyDays)))
        : undefined,
    facilityPayoff: payoff ? true : undefined,
    settlementDiscount,
    entryType: rest.entryType,
    paidAt: rest.paidAt ?? adminEntryIso(),
    note: rest.note,
    principalPortion: principalPortion !== undefined ? principalPortion : undefined,
    interestPortion: interestPortion !== undefined ? interestPortion : undefined,
    paymentAllocation: rest.paymentAllocation || undefined,
    carriedAfter: carriedAfter !== undefined ? carriedAfter : undefined,
    flexibleEmi: flexibleEmi || undefined,
    paymentVia: rest.paymentVia || undefined,
    companyBankId: rest.companyBankId || undefined,
    companyBankLabel: rest.companyBankLabel || undefined,
    paidBy: rest.paidBy || undefined,
    cashAmount: rest.cashAmount != null ? Math.max(0, Number(rest.cashAmount) || 0) : undefined,
    bankAmount: rest.bankAmount != null ? Math.max(0, Number(rest.bankAmount) || 0) : undefined,
  };
  list.unshift(row);
  saveDevJson(ADMIN_EMI_KEY, list);
  emit();
  return persistEmiToServerOrRollback(row);
}

export type RecordOverdueWaiverInput = {
  customerId: string;
  recordId: string;
  facilityTitle: string;
  kind: 'finance' | 'handloan';
  /** Amount to waive / reduce from outstanding overdue balance. */
  amount: number;
  reason: string;
  paidAt?: string;
  facility?: UnifiedLoanFacility;
};

/** Record an admin waiver or reduction of formula-based overdue charges (audit reason required). */
export async function recordOverdueWaiver(entry: RecordOverdueWaiverInput): Promise<RecordPaymentResult> {
  // Paise matter here: "customer pays exactly ₹10,000" needs a reduction like ₹2,361.63.
  const amount = Math.max(0, Math.round((Number(entry.amount) || 0) * 100) / 100);
  const reason = String(entry.reason || '').trim();
  if (amount <= 0) return { ok: false, error: 'Enter the waiver amount.' };
  if (!reason) return { ok: false, error: 'A reason is required for overdue waivers.' };

  if (entry.facility) {
    const snap = getFacilityCollectionSnapshot(entry.facility);
    if (snap.isLedgerClosed) {
      return { ok: false, error: 'This facility is already closed. No further overdue adjustments are allowed.' };
    }
    const outstanding =
      Math.round(computeFacilityUnpaidOverdue(entry.facility, paymentsForFacility(entry.recordId)).penaltyAmount * 100) / 100;
    if (outstanding <= 0) {
      return { ok: false, error: 'There is no outstanding overdue charge on this facility.' };
    }
    if (amount > outstanding + 0.01) {
      return {
        ok: false,
        error: `Cannot waive more than ₹${outstanding.toLocaleString('en-IN', { maximumFractionDigits: 2 })} outstanding overdue on this facility.`,
      };
    }
  }

  const row: AdminEmiPayment = {
    id: crypto.randomUUID(),
    customerId: entry.customerId,
    recordId: entry.recordId,
    facilityTitle: entry.facilityTitle,
    kind: entry.kind,
    amount: 0,
    creditedMonths: 0,
    overdueWaivedAmount: amount,
    entryType: 'overdue_waive',
    paidAt: entry.paidAt ?? adminEntryIso(),
    note: reason,
  };
  const list = readAdminEmiPayments();
  list.unshift(row);
  saveDevJson(ADMIN_EMI_KEY, list);
  emit();
  return persistEmiToServerOrRollback(row);
}

export function listAdminPaymentsForCustomer(customerId: string): AdminEmiPayment[] {
  return readAdminEmiPayments().filter((p) => p.customerId === customerId);
}

/** Whether every EMI is settled and the file can be administratively closed. */
export function canCloseFacilityFile(f: UnifiedLoanFacility): RecordPaymentResult {
  if (f.kind !== 'finance') {
    return { ok: false, error: 'File closure applies to vehicle finance facilities only.' };
  }
  const snap = getFacilityCollectionSnapshot(f);
  if (snap.isLedgerClosed) {
    return { ok: false, error: 'This file is already closed on the ledger.' };
  }
  const cashToward = cashCollectedForFacility(f, paymentsForFacility(f.recordId));
  const unpaidPrincipal = Math.max(0, snap.principal - Math.round(cashToward));
  if (unpaidPrincipal > 0.5) {
    return {
      ok: false,
      error: `Cannot close — ₹${unpaidPrincipal.toLocaleString('en-IN')} principal is still outstanding (collected ₹${Math.round(cashToward).toLocaleString('en-IN')} of ₹${snap.principal.toLocaleString('en-IN')}). Use Collect EMI / full settlement with the full outstanding amount.`,
    };
  }
  if (snap.remainingInstallments > 0 || snap.carriedBalance > 0) {
    return {
      ok: false,
      error: `Cannot close — ${snap.remainingInstallments} EMI installment(s) are still unpaid and ₹${Math.max(0, snap.carriedBalance).toLocaleString('en-IN')} balance remains on the schedule.`,
    };
  }
  if (snap.settlementCashFloor > 0.5) {
    return {
      ok: false,
      error: `Cannot close — ₹${snap.settlementCashFloor.toLocaleString('en-IN')} is still outstanding on this file.`,
    };
  }
  if (snap.accruedLateFees > 0) {
    return {
      ok: false,
      error: `Cannot close — ₹${snap.accruedLateFees.toLocaleString('en-IN')} late / penal charges are still outstanding. Settle them first.`,
    };
  }
  if (snap.creditSlots > 0) {
    return {
      ok: false,
      error: 'Cannot close — prepaid EMI credit is on file. Reconcile before closing the file.',
    };
  }
  return { ok: true };
}

/**
 * Close a finance file after all EMIs are settled (no unpaid slots, no late balance, no advance credits).
 * Posts a zero-amount ledger closure row and stamps the finance record as closed.
 */
export async function closeFacilityFile(f: UnifiedLoanFacility, customerId: string): Promise<RecordPaymentResult> {
  const check = canCloseFacilityFile(f);
  if (!check.ok) return check;

  const snap = getFacilityCollectionSnapshot(f);
  const split =
    snap.remainingInstallments > 0
      ? linearSplitForMonths(snap.principal, snap.periods, snap.lifetimeInterestEstimate, snap.remainingInstallments)
      : { principalPortion: 0, interestPortion: 0 };

  const list = readAdminEmiPayments();
  const row: AdminEmiPayment = {
    id: crypto.randomUUID(),
    customerId,
    recordId: f.recordId,
    facilityTitle: f.title,
    kind: f.kind,
    amount: 0,
    creditedMonths: 0,
    facilityPayoff: true,
    entryType: 'lump_sum_close',
    paidAt: adminEntryIso(),
    note: 'File closed — all EMIs settled',
    principalPortion: split.principalPortion || undefined,
    interestPortion: split.interestPortion || undefined,
  };
  list.unshift(row);
  saveDevJson(ADMIN_EMI_KEY, list);
  emit();

  const synced = await persistEmiToServerOrRollback(row);
  if (!synced.ok) return synced;

  const rec = readFinanceRecords().find((r) => r.id === f.recordId);
  if (rec) {
    upsertFinanceRecord({
      ...rec,
      updatedAt: new Date().toISOString(),
      data: {
        ...rec.data,
        closed: 'yes',
        closedDate: adminEntryDateOnly(),
      },
    });
  } else {
    emit();
  }
  return { ok: true };
}

export type FinanceFacilityMeta = {
  seized: boolean;
  seizedDate: string;
  seizedReason: string;
  closedOnRecord: boolean;
  closedDate: string;
};

export function getFinanceFacilityMeta(recordId: string): FinanceFacilityMeta | null {
  const rec = readFinanceRecords().find((r) => r.id === recordId);
  if (!rec) return null;
  const d = rec.data;
  return {
    seized: String(d.seized ?? '').toLowerCase() === 'yes',
    seizedDate: String(d.seizedDate ?? ''),
    seizedReason: String(d.seizedReason ?? ''),
    closedOnRecord: String(d.closed ?? '').toLowerCase() === 'yes',
    closedDate: String(d.closedDate ?? ''),
  };
}

/** Mark a finance vehicle as seized with a mandatory written reason. */
export function markFacilitySeized(recordId: string, reason: string, seizedDate?: string): RecordPaymentResult {
  const reasonTrim = reason.trim();
  if (reasonTrim.length < 5) {
    return { ok: false, error: 'Enter a clear reason for seizure (at least 5 characters).' };
  }
  const rec = readFinanceRecords().find((r) => r.id === recordId);
  if (!rec) return { ok: false, error: 'Finance record not found.' };
  if (String(rec.data.seized ?? '').toLowerCase() === 'yes') {
    return { ok: false, error: 'This vehicle is already marked as seized.' };
  }
  upsertFinanceRecord({
    ...rec,
    updatedAt: new Date().toISOString(),
    data: {
      ...rec.data,
      seized: 'yes',
      seizedDate: seizedDate || adminEntryDateOnly(),
      seizedReason: reasonTrim,
    },
  });
  return { ok: true };
}

export function releaseFacilitySeized(recordId: string): RecordPaymentResult {
  const rec = readFinanceRecords().find((r) => r.id === recordId);
  if (!rec) return { ok: false, error: 'Finance record not found.' };
  if (String(rec.data.seized ?? '').toLowerCase() !== 'yes') {
    return { ok: false, error: 'This vehicle is not marked as seized.' };
  }
  upsertFinanceRecord({
    ...rec,
    updatedAt: new Date().toISOString(),
    data: {
      ...rec.data,
      seized: 'no',
      seizedReleasedAt: adminEntryDateOnly(),
    },
  });
  return { ok: true };
}

export function readSeizedFinanceRecords(): FinanceRecord[] {
  return readFinanceRecords().filter((r) => String(r.data?.seized ?? '').toLowerCase() === 'yes');
}

export function useAdminEmiPayments(): AdminEmiPayment[] {
  const [list, setList] = useState(readAdminEmiPayments);
  useEffect(() => {
    const fn = () => setList(readAdminEmiPayments());
    window.addEventListener(WORKSPACE_DATA_EVENT, fn);
    return () => window.removeEventListener(WORKSPACE_DATA_EVENT, fn);
  }, []);
  return list;
}

export type FinanceFormSnapshot = Record<string, unknown> & {
  hasDocuments?: boolean;
};

export type FinanceRecord = {
  id: string;
  createdAt: string;
  updatedAt: string;
  source: 'api' | 'local';
  data: FinanceFormSnapshot;
};

export type HandLoanFormSnapshot = Record<string, unknown>;

export type HandLoanRecord = {
  id: string;
  createdAt: string;
  updatedAt: string;
  source: 'api' | 'local';
  data: HandLoanFormSnapshot;
};

export function serializeFinanceForm(form: Record<string, unknown>): FinanceFormSnapshot {
  const { documents: _d, ...rest } = form;
  return {
    ...rest,
    hasDocuments: !!(form.documents && typeof (form.documents as FileList).length === 'number' && (form.documents as FileList).length > 0),
  };
}

export function serializeHandLoanForm(form: Record<string, unknown>): HandLoanFormSnapshot {
  const { aadharOriginal, supportingDocs, suretyImage, suretyAadhar, loanDetails, ...rest } = form;
  const details =
    loanDetails instanceof Map
      ? Object.fromEntries(loanDetails as Map<string, string>)
      : (loanDetails as Record<string, string>) ?? {};
  return {
    ...rest,
    loanDetails: details,
    hasFiles: !!(aadharOriginal || supportingDocs || suretyImage || suretyAadhar),
  };
}

export function readFinanceRecords(): FinanceRecord[] {
  return sortByEntryDateDesc(loadDevJson<FinanceRecord[]>(FIN_KEY, []));
}

export function readHandLoanRecords(): HandLoanRecord[] {
  return sortByEntryDateDesc(loadDevJson<HandLoanRecord[]>(HAND_KEY, []));
}

function writeFinances(list: FinanceRecord[]) {
  saveDevJson(FIN_KEY, list);
  emit();
}

function writeHands(list: HandLoanRecord[]) {
  saveDevJson(HAND_KEY, list);
  emit();
}

export function upsertFinanceRecord(record: FinanceRecord) {
  const list = readFinanceRecords();
  const i = list.findIndex((r) => r.id === record.id);
  if (i >= 0) list[i] = record;
  else list.unshift(record);
  writeFinances(list);
  cacheFinanceApplicantPhoto(record.data as Record<string, unknown>);
  cloud(() => financeApi.upsert({ id: record.id, source: record.source, data: record.data }));
}

export function deleteFinanceRecord(id: string) {
  writeFinances(readFinanceRecords().filter((r) => r.id !== id));
  cloud(() => financeApi.remove(id));
}

export function upsertHandLoanRecord(record: HandLoanRecord) {
  const list = readHandLoanRecords();
  const i = list.findIndex((r) => r.id === record.id);
  if (i >= 0) list[i] = record;
  else list.unshift(record);
  writeHands(list);
  cloud(() => handLoanApi.upsert({ id: record.id, source: record.source, data: record.data }));
}

/**
 * Bulk import: saves locally, then waits for the server. If the server refuses the record,
 * the local copy is removed again so the screen never shows a loan the server doesn't have.
 */
export async function saveImportedLoanRecord(kind: 'finance' | 'handloan', record: FinanceRecord | HandLoanRecord): Promise<void> {
  const body = { id: record.id, source: record.source, data: record.data };
  if (kind === 'finance') {
    writeFinances([record, ...readFinanceRecords().filter((r) => r.id !== record.id)]);
    cacheFinanceApplicantPhoto(record.data as Record<string, unknown>);
  } else {
    writeHands([record, ...readHandLoanRecords().filter((r) => r.id !== record.id)]);
  }
  if (!hasAuthToken()) return;
  try {
    if (kind === 'finance') await financeApi.upsert(body);
    else await handLoanApi.upsert(body);
  } catch (e) {
    if (kind === 'finance') writeFinances(readFinanceRecords().filter((r) => r.id !== record.id));
    else writeHands(readHandLoanRecords().filter((r) => r.id !== record.id));
    throw e;
  }
}

export function deleteHandLoanRecord(id: string) {
  writeHands(readHandLoanRecords().filter((r) => r.id !== id));
  cloud(() => handLoanApi.remove(id));
}

/** Optional log line for debugging */
export function logFinanceSubmit(entry: Record<string, unknown>) {
  appendDevLog('finance_submit_log', entry);
}

export type UnifiedLoanFacility = {
  recordId: string;
  kind: 'finance' | 'handloan';
  title: string;
  /** Finance HA / agreement number — used as SMS Ref ID with the finance business name. */
  haNumber?: string;
  principal: number;
  emi: number;
  periods: number;
  startDate: string;
  /** Hand loan: short-term lump sum vs monthly EMI collections. */
  repaymentType?: 'short_term' | 'emi';
  /** Hand loan short-term: days until full repayment. */
  termDays?: number;
  /** Hand loan interest rate (₹ per ₹100 principal / month). */
  interestRate?: number;
  /** Hand loan: simple vs compound interest. */
  interestType?: 'simple' | 'compound';
  /** Migrated hand loan: interest due recorded at cutover (books opening). */
  openingInterestDue?: number;
  /** Original sanctioned principal (migrated loans). Used for profile total when set. */
  originalPrincipal?: number;
  /** Cash already collected before cutover (migrated loans) — added to Amount collected. */
  preCutoverCollected?: number;
  /** Vehicle / asset total before out payment. */
  totalAmount?: number;
  /** Down / out payment (excluded from EMI principal). */
  outPayment?: number;
  /** Out-payment interest: rupees per ₹100 OP principal per month. */
  opInterest?: number;
};

export type UnifiedCustomer = {
  id: string;
  /** Phone or short name ref for UI/exports (not the internal aggregation key). */
  displayRef: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  /** ISO timestamp of the first finance / hand-loan entry for this borrower. */
  createdAt: string;
  facilities: UnifiedLoanFacility[];
  totalLoan: number;
  paidLoan: number;
  /** Total contract still outstanding (remaining installments + carry deficit), before penal charges. */
  pendingLoan: number;
  /** Principal still open under linear contract model (for profile cards). */
  principalOutstanding: number;
  /** Sum of logged late / penal charges across facilities (added on top of `pendingLoan` in UI). */
  accruedLateFees: number;
  /** Total late / delayed days logged across this customer's payments. */
  lateDays: number;
  /** Count of installments marked Paid across facilities. */
  paidEmiCount: number;
  /** Count of installments still outstanding (Due + Scheduled) across facilities. */
  pendingEmiCount: number;
  paymentHistory: { date: string; amount: number; status: string }[];
  pendingEmi: { dueDate: string; amount: number };
  upcomingInstallments: { date: string; amount: number; status: string; facility: string }[];
};

export type CustomerInstallmentRow = {
  date: string;
  amount: number;
  status: string;
  facility: string;
  recordId: string;
  kind: 'finance' | 'handloan';
  /** 1-based installment number on this facility. */
  installmentNo?: number;
  /**
   * Running total of consecutive Due EMIs through this row
   * (e.g. EMI 2000 unpaid → next Due shows cumulativeDue 4000, then 6000).
   */
  cumulativeDue?: number;
  /** Date the slot was paid in full (YYYY-MM-DD), when known. */
  paidAt?: string;
};

/** Full merged schedule across facilities (for customer detail screens). */
export function getInstallmentScheduleForCustomer(c: UnifiedCustomer): CustomerInstallmentRow[] {
  const allRows: CustomerInstallmentRow[] = [];
  const payments = readAdminEmiPayments();
  const { payoffs } = reducePaymentMaps(payments);
  for (const f of c.facilities) {
    const paidSlots = paidSlotsForFacility(f, payoffs, 0, payments);
    buildFacilityInstallmentRows(f, paidSlots, payments).forEach((row) =>
      allRows.push({
        ...row,
        facility: f.title,
        recordId: f.recordId,
        kind: f.kind,
      })
    );
  }
  return allRows.sort((a, b) => a.date.localeCompare(b.date));
}

/** Full installment schedule for one aggregated customer using an explicit ledger. */
export function buildCustomerScheduleFrom(
  c: UnifiedCustomer,
  payments: AdminEmiPayment[],
  filterRecordId?: string | null
): CustomerInstallmentRow[] {
  const { payoffs } = reducePaymentMaps(payments);
  const rows: CustomerInstallmentRow[] = [];
  const facilities = filterRecordId && filterRecordId !== 'all'
    ? c.facilities.filter((f) => f.recordId === filterRecordId)
    : c.facilities;
  for (const f of facilities) {
    const paidSlots = paidSlotsForFacility(f, payoffs, 0, payments);
    buildFacilityInstallmentRows(f, paidSlots, payments).forEach((row) =>
      rows.push({ ...row, facility: f.title, recordId: f.recordId, kind: f.kind })
    );
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date));
}

function num(s: unknown, fallback = 0): number {
  const n = parseFloat(String(s ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

function int(s: unknown, fallback = 0): number {
  const n = parseInt(String(s ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

/** Build synthetic schedule using flexible EMI balance (local finance shop). */
export function buildInstallmentRows(
  startIso: string,
  emi: number,
  periods: number,
  paidSlots: number,
  options?: { carriedBalance?: number; paidTowardNext?: number; paidAtBySlot?: Map<number, string> }
): {
  date: string;
  amount: number;
  status: string;
  installmentNo: number;
  cumulativeDue?: number;
  paidAt?: string;
}[] {
  const rows: {
    date: string;
    amount: number;
    status: string;
    installmentNo: number;
    cumulativeDue?: number;
    paidAt?: string;
  }[] = [];
  const start = parseLoanStartDate(startIso);
  if (!start) return rows;
  const p = Math.max(0, Math.floor(periods));
  const e = Math.max(0, emi);
  const carried = Math.round(Number(options?.carriedBalance) || 0);
  const today = resolveWorkspaceAsOfDate() || '';
  // Cash already applied toward the first unpaid installment (FIFO remainder).
  let partialPaid = 0;
  if (options?.paidTowardNext != null) {
    partialPaid = Math.min(e, Math.max(0, Math.round(options.paidTowardNext)));
  } else if (e > 0) {
    if (carried > 0) partialPaid = Math.ceil(carried / e) * e - carried;
    else if (carried < 0) partialPaid = Math.abs(carried) % e;
  }

  let dueRun = 0;
  for (let i = 0; i < p; i++) {
    const dateStr = formatLocalYmd(addMonthsClamped(start, i + 1));
    let status = 'Scheduled';
    let amount = e;

    if (i < paidSlots) {
      status = 'Paid';
    } else {
      status = today && dateStr > today ? 'Scheduled' : 'Due';
      if (i === paidSlots) amount = Math.max(0, e - partialPaid);
    }

    let cumulativeDue: number | undefined;
    if (status === 'Due') {
      dueRun += amount;
      cumulativeDue = dueRun;
    }

    const paidAt = status === 'Paid' ? options?.paidAtBySlot?.get(i) : undefined;
    rows.push({ date: dateStr, amount, status, installmentNo: i + 1, cumulativeDue, paidAt });
  }
  return rows;
}

function paidSlotsForFacility(
  f: UnifiedLoanFacility,
  payoffs: Set<string>,
  _credit: number,
  payments?: AdminEmiPayment[]
): number {
  if (payoffs.has(f.recordId)) return f.periods;
  const pmt = payments ?? readAdminEmiPayments();
  return replayFlexibleEmiState(f, pmt).paidCycles;
}

export function buildFacilityInstallmentRows(
  f: UnifiedLoanFacility,
  paidSlots: number,
  payments?: AdminEmiPayment[]
): {
  date: string;
  amount: number;
  status: string;
  installmentNo?: number;
  cumulativeDue?: number;
  paidAt?: string;
}[] {
  const pmt = payments ?? readAdminEmiPayments();
  const flex = replayFlexibleEmiState(f, pmt);

  if (f.repaymentType === 'short_term') {
    const days = Math.max(1, f.termDays ?? 30);
    const startYmd = String(f.startDate || '').trim().slice(0, 10);
    const due = new Date(/^\d{4}-\d{2}-\d{2}$/.test(startYmd) ? `${startYmd}T12:00:00` : f.startDate);
    if (!Number.isFinite(due.getTime())) return [];
    due.setDate(due.getDate() + days);
    const y = due.getFullYear();
    const m = String(due.getMonth() + 1).padStart(2, '0');
    const day = String(due.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${day}`;
    // Cleared principal+interest (or payoff) → no upcoming cashflow row.
    if (flex.isComplete || flex.isPayoff || flex.amountOwed <= 0.5) {
      const lastPay = [...pmt]
        .filter((p) => p.recordId === f.recordId)
        .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())[0];
      return [
        {
          date: dateStr,
          amount: Math.max(0, Math.round(Number(f.principal) || 0)),
          status: 'Paid',
          installmentNo: 1,
          paidAt: lastPay?.paidAt?.slice(0, 10),
        },
      ];
    }
    const dueMs = new Date(`${dateStr}T12:00:00`).getTime();
    let status = 'Scheduled';
    const booksMs = resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
    if (booksMs >= dueMs) status = 'Due';
    const amount = Math.max(0, Math.round(flex.amountOwed));
    return [
      {
        date: dateStr,
        amount,
        status,
        installmentNo: 1,
        cumulativeDue: status === 'Due' ? amount : undefined,
      },
    ];
  }

  // Lazy import avoided — slot map lives in overdue.ts; replicate light paid-at map here.
  const paidAtBySlot = new Map<number, string>();
  {
    const emi = Math.max(0, Math.round(Number(f.emi) || 0));
    const periods = Math.max(1, Math.floor(Number(f.periods) || 1));
    const facilityPayments = pmt
      .filter((p) => p.recordId === f.recordId && p.entryType !== 'overdue_waive')
      .sort((a, b) => new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime());
    let cumulative = 0;
    let covered = 0;
    for (const pay of facilityPayments) {
      const paidDate = String(pay.paidAt || '').slice(0, 10);
      if (pay.facilityPayoff) {
        for (let i = 0; i < periods; i++) {
          if (!paidAtBySlot.has(i) && paidDate) paidAtBySlot.set(i, paidDate);
        }
        covered = periods;
        continue;
      }
      if (emi <= 0) continue;
      cumulative += emiCashAppliedFromPayment(pay, emi);
      const nowCovered = Math.max(0, Math.min(periods, Math.floor((cumulative + 0.005) / emi)));
      for (let i = covered; i < nowCovered; i++) {
        if (paidDate) paidAtBySlot.set(i, paidDate);
      }
      covered = nowCovered;
    }
  }

  if (f.kind === 'handloan') {
    const emi = Math.max(0, Math.round(Number(f.emi) || 0));
    const slots = flex.isComplete ? f.periods : paidSlots;
    const remainder = flex.isComplete ? 0 : handEmiCashApplied(f, pmt) - slots * emi;
    return buildInstallmentRows(f.startDate, f.emi, f.periods, slots, {
      paidTowardNext: remainder,
      paidAtBySlot,
    });
  }

  return buildInstallmentRows(f.startDate, f.emi, f.periods, paidSlots, {
    carriedBalance: flex.amountOwed,
    paidAtBySlot,
  });
}

export function aggregateCustomers(): UnifiedCustomer[] {
  return aggregateCustomersFrom(readFinanceRecords(), readHandLoanRecords(), readAdminEmiPayments());
}

/**
 * Record IDs (finance or hand-loan) whose account is still "pending": the ledger
 * is not closed AND there are unpaid EMI installments or unsettled late/penal
 * charges. Deleting such a file would erase a borrower who still owes money, so
 * the UI blocks deletion until the file is fully paid / settled / closed.
 */
export function pendingLoanRecordIds(): Set<string> {
  const ids = new Set<string>();
  for (const customer of aggregateCustomers()) {
    for (const facility of customer.facilities) {
      const snap = getFacilityCollectionSnapshot(facility);
      if (snap.isLedgerClosed) continue;
      if (facility.kind === 'handloan') {
        const pOut = snap.handPrincipalOutstanding ?? 0;
        const iDue = snap.handInterestDue ?? 0;
        if (pOut <= 0.5 && iDue <= 0.5 && snap.accruedLateFees <= 0.5) continue;
        ids.add(facility.recordId);
        continue;
      }
      if (snap.remainingInstallments > 0 || snap.carriedBalance > 0 || snap.accruedLateFees > 0) {
        ids.add(facility.recordId);
      }
    }
  }
  return ids;
}

/** True when a specific finance / hand-loan record still has pending dues. */
export function isLoanRecordPending(recordId: string): boolean {
  return pendingLoanRecordIds().has(recordId);
}

/** Reduce a payments list into the per-record maps used by aggregation. */
function reducePaymentMaps(payments: AdminEmiPayment[]) {
  const extraByRec: Record<string, number> = {};
  const payoffs = new Set<string>();
  const lateMap: Record<string, number> = {};
  const lateDaysByRec: Record<string, number> = {};
  for (const p of payments) {
    if (p.facilityPayoff) payoffs.add(p.recordId);
    else if (!p.flexibleEmi && p.carriedAfter === undefined) {
      const c = Math.trunc(Number(p.creditedMonths) || 0);
      if (c !== 0) extraByRec[p.recordId] = (extraByRec[p.recordId] ?? 0) + c;
    }
    const charged = Math.max(0, Number(p.lateFeeAmount) || 0);
    const collected = Math.max(0, Number(p.lateFeeCollected) || 0);
    if (charged > 0 || collected > 0) {
      lateMap[p.recordId] = (lateMap[p.recordId] ?? 0) + charged - collected;
    }
    const pd = Math.max(0, Math.floor(Number(p.penaltyDays) || 0));
    if (pd > 0) lateDaysByRec[p.recordId] = (lateDaysByRec[p.recordId] ?? 0) + pd;
  }
  for (const id of Object.keys(lateMap)) {
    lateMap[id] = Math.max(0, Math.round(lateMap[id]));
  }
  return { extraByRec, payoffs, lateMap, lateDaysByRec };
}

/**
 * Pure aggregation over explicit data (finance + hand-loan records + EMI ledger).
 * Used by `aggregateCustomers()` (localStorage) and by the read-only customer
 * portal (backend-fetched data) so both compute identical figures.
 */
export function aggregateCustomersFrom(
  finances: FinanceRecord[],
  hands: HandLoanRecord[],
  payments: AdminEmiPayment[]
): UnifiedCustomer[] {
  const map = new Map<string, UnifiedCustomer>();

  const addFacility = (key: string, patch: Partial<UnifiedCustomer> & { facility: UnifiedLoanFacility }) => {
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        id: key,
        displayRef: '',
        name: patch.name ?? 'Customer',
        email: patch.email ?? genCustomerLoginEmail(patch.name ?? 'Customer', patch.phone ?? key),
        phone: patch.phone ?? '',
        avatar: patch.avatar ?? '',
        createdAt: '',
        facilities: [patch.facility],
        totalLoan: 0,
        paidLoan: 0,
        pendingLoan: 0,
        principalOutstanding: 0,
        accruedLateFees: 0,
        lateDays: 0,
        paidEmiCount: 0,
        pendingEmiCount: 0,
        paymentHistory: [],
        pendingEmi: { dueDate: '', amount: 0 },
        upcomingInstallments: [],
      });
    } else {
      existing.facilities.push(patch.facility);
      if (patch.name) existing.name = patch.name;
      if (patch.phone) existing.phone = patch.phone;
      if (patch.email) existing.email = patch.email;
      if (patch.avatar && !existing.avatar) existing.avatar = patch.avatar;
    }
  };

  for (const r of finances) {
    const d = r.data;
    const phone = String(d.mobileNumber ?? '').trim() || `fin-${r.id}`;
    const name = String(d.customerName ?? 'Borrower').trim();
    const totalAmount = num(d.totalAmount || d.amount);
    const outPayment = num(d.outPayment);
    const balance = Math.max(0, totalAmount - outPayment);
    const principal = balance > 0 ? balance : totalAmount;
    const emi = num(d.emiAmount);
    const periods = int(d.emiPeriod, 12);
    const originalPrincipal = num(d.originalPrincipal);
    const openingOut = num(d.openingPrincipalOutstanding);
    const emisPaidBefore = int(d.emisPaidBeforeCutover, 0);
    const calc = computeFinance({
      totalAmount,
      outPayment,
      interestPercentage: d.interestPercentage,
      emiPeriod: periods,
    });
    const emiRounded = emi || calc.emi || (principal > 0 && periods > 0 ? Math.round(principal / periods) : 0);
    let preCutoverCollected = num(d.preCutoverCollected);
    if (preCutoverCollected <= 0 && originalPrincipal > 0 && openingOut > 0) {
      preCutoverCollected = Math.max(0, originalPrincipal - openingOut);
    }
    if (preCutoverCollected <= 0 && emisPaidBefore > 0 && emiRounded > 0) {
      preCutoverCollected = Math.round(emisPaidBefore * emiRounded);
    }
    addFacility(`ph:${phone}`, {
      name,
      phone: String(d.mobileNumber ?? ''),
      email: genCustomerLoginEmail(name, String(d.mobileNumber ?? '')),
      avatar: mediaSrc(String(d.applicantPhoto ?? '')),
      facility: {
        recordId: r.id,
        kind: 'finance',
        title: `Finance · ${d.regNo || 'Vehicle'}`,
        haNumber: String(d.haNumber ?? '').trim() || undefined,
        principal,
        emi: emiRounded,
        periods: periods || 1,
        startDate: String(d.agreementDate || d.startDate || r.createdAt),
        originalPrincipal: originalPrincipal || undefined,
        preCutoverCollected: preCutoverCollected || undefined,
        totalAmount: totalAmount || undefined,
        outPayment: outPayment || undefined,
        opInterest: num(d.opInterest) || undefined,
      },
    });
  }

  for (const r of hands) {
    const d = r.data;
    const name = String(d.name ?? 'Borrower').trim();
    const phoneRaw = String(d.mobileNumber ?? d.suretyNumber ?? d.aadharNumber ?? '').trim();
    const phoneKey = phoneRaw || `hl-${r.id.slice(0, 8)}`;
    const key = `ph:${phoneKey}`;
    const principal = num(d.loanAmount);
    const rate = num(d.interestRate);
    const startDate = new Date(String(d.agreementDate || d.startDate || r.createdAt));
    const repaymentType = parseHandLoanRepaymentType(d.repaymentType, d.interestBasis);
    const facilityStart = String(d.agreementDate || d.startDate || r.createdAt);
    const originalPrincipal = num(d.originalPrincipal);
    const openingOut = num(d.openingPrincipalOutstanding);
    const emisPaidBefore = int(d.emisPaidBeforeCutover, 0);
    let preCutoverCollected = num(d.preCutoverCollected);
    if (preCutoverCollected <= 0 && originalPrincipal > 0 && openingOut > 0) {
      preCutoverCollected = Math.max(0, originalPrincipal - openingOut);
    }

    if (repaymentType === 'short_term') {
      const days = resolveShortTermDays(d.loanTerm, repaymentType, d.interestBasis, startDate);
      const calc = computeShortTermHandLoan(principal, rate, days);
      const totalPayable = calc?.totalPayable ?? principal;
      addFacility(key, {
        name,
        phone: phoneRaw || String(d.suretyNumber ?? d.aadharNumber ?? '—'),
        email: genCustomerLoginEmail(name, phoneRaw || String(d.suretyNumber ?? d.aadharNumber ?? '')),
        avatar: mediaSrc(String(d.applicantPhoto ?? '')),
        facility: {
          recordId: r.id,
          kind: 'handloan',
          title: `Hand loan · ${d.loanType || 'Personal'} (${days}d)`,
          principal,
          emi: totalPayable,
          periods: 1,
          startDate: facilityStart,
          repaymentType: 'short_term',
          termDays: days,
          interestRate: rate,
          interestType: parseHandLoanInterestType(d.interestType),
          openingInterestDue: num(d.openingInterestDue) || undefined,
          originalPrincipal: originalPrincipal || undefined,
          preCutoverCollected: preCutoverCollected || undefined,
        },
      });
    } else {
      const months = Math.max(1, int(d.loanTerm, 12));
      const calc = computeEmiHandLoan(principal, rate, months);
      const emi = calc?.emi ?? (principal && months ? Math.round(principal / months) : 0);
      if (preCutoverCollected <= 0 && emisPaidBefore > 0 && emi > 0) {
        preCutoverCollected = Math.round(emisPaidBefore * emi);
      }
      addFacility(key, {
        name,
        phone: phoneRaw || String(d.suretyNumber ?? d.aadharNumber ?? '—'),
        email: genCustomerLoginEmail(name, phoneRaw || String(d.suretyNumber ?? d.aadharNumber ?? '')),
        avatar: mediaSrc(String(d.applicantPhoto ?? '')),
        facility: {
          recordId: r.id,
          kind: 'handloan',
          title: `Hand loan · ${d.loanType || 'Personal'} (EMI)`,
          principal,
          emi,
          periods: months,
          startDate: facilityStart,
          repaymentType: 'emi',
          interestRate: rate,
          interestType: parseHandLoanInterestType(d.interestType),
          openingInterestDue: num(d.openingInterestDue) || undefined,
          originalPrincipal: originalPrincipal || undefined,
          preCutoverCollected: preCutoverCollected || undefined,
        },
      });
    }
  }

  const customers = [...map.values()];
  const { payoffs } = reducePaymentMaps(payments);
  for (const c of customers) {
    let total = 0;
    let paid = 0;
    let lateFeesTotal = 0;
    let lateDaysTotal = 0;
    let paidEmiCount = 0;
    let pendingEmiCount = 0;
    let contractOutstandingTotal = 0;
    let principalOutstandingTotal = 0;
    const allRows: { date: string; amount: number; status: string; facility: string }[] = [];
    for (const f of c.facilities) {
      total += f.originalPrincipal && f.originalPrincipal > 0 ? f.originalPrincipal : f.principal;
      const overdueSnap = computeFacilityUnpaidOverdue(f, payments);
      lateFeesTotal += overdueSnap.penaltyAmount;
      lateDaysTotal += overdueSnap.overdueDays;
      const flex = replayFlexibleEmiState(f, payments);
      const paidSlots = paidSlotsForFacility(f, payoffs, 0, payments);
      const periodsRounded = Math.max(1, f.periods);
      paid += cashCollectedForFacility(f, payments, payoffs) + Math.max(0, Number(f.preCutoverCollected) || 0);
      const remainingInst = Math.max(0, periodsRounded - paidSlots);
      const principalPaidLinear =
        periodsRounded > 0 ? Math.round((f.principal * paidSlots) / periodsRounded) : 0;
      if (f.kind === 'handloan') {
        const facilityPays = payments.filter((p) => p.recordId === f.recordId);
        const bal = replayHandLoanBalances({
          principal: f.principal,
          rate: Number(f.interestRate) || 0,
          interestType: parseHandLoanInterestType(f.interestType),
          repaymentType: f.repaymentType === 'short_term' ? 'short_term' : 'emi',
          startDate: f.startDate,
          termDays: f.termDays,
          periods: f.periods,
          payments: facilityPays,
          asOf: handLoanLedgerAsOfMs(f.startDate, facilityPays, resolveWorkspaceAsOfDate() || undefined),
        });
        const openingInterestSeed = Math.max(0, Math.round(Number(f.openingInterestDue) || 0));
        const hasPostCutoverInterestPay = facilityPays.some(
          (p) => Math.max(0, Number(p.interestPortion) || 0) > 0 || p.facilityPayoff
        );
        const interestDue = bal.interestDue + (hasPostCutoverInterestPay ? 0 : openingInterestSeed);
        principalOutstandingTotal += bal.principalOutstanding;
        contractOutstandingTotal += bal.principalOutstanding + interestDue;
      } else {
        contractOutstandingTotal += remainingFinanceContractCash(f, flex);
        principalOutstandingTotal += Math.max(0, f.principal - principalPaidLinear);
      }
      paidEmiCount += paidSlots;
      pendingEmiCount += remainingInst;
      buildFacilityInstallmentRows(f, paidSlots, payments).forEach((row) =>
        allRows.push({ ...row, facility: f.title })
      );
    }
    c.totalLoan = Math.round(total);
    c.paidLoan = Math.round(paid);
    c.accruedLateFees = Math.round(lateFeesTotal);
    c.lateDays = lateDaysTotal;
    c.paidEmiCount = paidEmiCount;
    c.pendingEmiCount = pendingEmiCount;
    c.principalOutstanding = Math.round(principalOutstandingTotal);
    c.pendingLoan = Math.round(contractOutstandingTotal + c.accruedLateFees);
    c.paymentHistory = allRows
      .filter((x) => x.status === 'Paid')
      .slice(-12)
      .map((x) => ({ date: x.date, amount: x.amount, status: x.status }));
    const dueRows = allRows.filter((x) => x.status === 'Due');
    const firstSched = allRows.find((x) => x.status === 'Scheduled');
    const nextUnpaid = dueRows[0] ?? firstSched;
    c.pendingEmi = {
      dueDate: nextUnpaid?.date || '—',
      // Amount due now = all arrears across overdue installments (else the next EMI).
      amount: dueRows.length ? dueRows.reduce((s, r) => s + r.amount, 0) : nextUnpaid?.amount || 0,
    };
    c.upcomingInstallments = allRows
      .filter((x) => x.status !== 'Paid')
      .slice(0, 6)
      .map((x) => ({ date: x.date, amount: x.amount, status: x.status, facility: x.facility }));
    c.createdAt = customerAccountCreatedAt(c);
  }

  const sorted = sortByEntryDateDesc(customers);
  sorted.forEach((c, i) => {
    c.displayRef = computeCustomerDisplayRef(c, i);
  });
  return sorted;
}

export function useFinanceRecords(): FinanceRecord[] {
  const [list, setList] = useState(readFinanceRecords);
  useEffect(() => {
    const fn = () => setList(readFinanceRecords());
    window.addEventListener(WORKSPACE_DATA_EVENT, fn);
    return () => window.removeEventListener(WORKSPACE_DATA_EVENT, fn);
  }, []);
  return list;
}

export function useHandLoanRecords(): HandLoanRecord[] {
  const [list, setList] = useState(readHandLoanRecords);
  useEffect(() => {
    const fn = () => setList(readHandLoanRecords());
    window.addEventListener(WORKSPACE_DATA_EVENT, fn);
    return () => window.removeEventListener(WORKSPACE_DATA_EVENT, fn);
  }, []);
  return list;
}

export function useUnifiedCustomers(): UnifiedCustomer[] {
  const [list, setList] = useState(aggregateCustomers);
  useEffect(() => {
    const fn = () => setList(aggregateCustomers());
    window.addEventListener(WORKSPACE_DATA_EVENT, fn);
    return () => window.removeEventListener(WORKSPACE_DATA_EVENT, fn);
  }, []);
  return list;
}
